import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleDashed,
  Clock,
  Info,
  Loader,
  type LucideIcon,
} from 'lucide-react'
import type { HTMLAttributes } from 'react'

import { cn } from '@/lib/cn'
import type { ProjectStatus, TaskStatus } from '@/types/api'

const badgeVariants = cva(
  [
    'inline-flex items-center gap-1.5 whitespace-nowrap',
    'rounded-full border px-2.5 py-0.5 text-[11px] font-medium leading-5',
    '[&_svg]:size-3 [&_svg]:shrink-0',
  ],
  {
    variants: {
      tone: {
        neutral: 'border-line-subtle bg-overlay-strong text-text-secondary',
        brand: 'border-brand-500/30 bg-brand-500/15 text-brand-200',
        info: 'border-info-500/30 bg-info-500/15 text-info-300',
        success: 'border-success-500/30 bg-success-500/15 text-success-300',
        warning: 'border-warning-500/30 bg-warning-500/15 text-warning-300',
        danger: 'border-danger-500/30 bg-danger-500/15 text-danger-300',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ tone, className, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {children}
    </span>
  )
}

interface StatusDefinition {
  label: string
  tone: BadgeTone
  icon: LucideIcon
}

/**
 * Statusdefinitioner ét sted, så samme status altid ser ens ud og har samme
 * danske betegnelse i hele systemet.
 *
 * Hver status har både farve, ikon og tekst. Farven må aldrig stå alene –
 * det ville udelukke brugere med farveblindhed (WCAG 1.4.1).
 */
const PROJECT_STATUS: Record<ProjectStatus, StatusDefinition> = {
  Draft: { label: 'Kladde', tone: 'neutral', icon: CircleDashed },
  Planning: { label: 'Planlægning', tone: 'info', icon: Clock },
  Active: { label: 'Aktiv', tone: 'success', icon: CheckCircle2 },
  OnHold: { label: 'På pause', tone: 'warning', icon: AlertTriangle },
  Completed: { label: 'Afsluttet', tone: 'brand', icon: CheckCircle2 },
  Archived: { label: 'Arkiveret', tone: 'neutral', icon: Ban },
}

const TASK_STATUS: Record<TaskStatus, StatusDefinition> = {
  Todo: { label: 'Ikke startet', tone: 'neutral', icon: CircleDashed },
  InProgress: { label: 'I gang', tone: 'info', icon: Loader },
  Blocked: { label: 'Blokeret', tone: 'danger', icon: Ban },
  InReview: { label: 'Til kontrol', tone: 'warning', icon: Info },
  Done: { label: 'Færdig', tone: 'success', icon: CheckCircle2 },
  Cancelled: { label: 'Annulleret', tone: 'neutral', icon: Ban },
}

export interface StatusBadgeProps {
  kind: 'project' | 'task'
  status: string
  className?: string
  /** Skjul ikonet i meget tætte tabeller. Teksten bevares altid. */
  hideIcon?: boolean
}

export function StatusBadge({ kind, status, className, hideIcon }: StatusBadgeProps) {
  const definitions = kind === 'project' ? PROJECT_STATUS : TASK_STATUS
  const definition = (definitions as Record<string, StatusDefinition | undefined>)[status]

  // Ukendte statusser vises som rå værdi frem for at forsvinde. Det gør en
  // manglende oversættelse synlig i stedet for tavs.
  if (!definition) {
    return (
      <Badge tone="neutral" className={className}>
        {status}
      </Badge>
    )
  }

  const Icon = definition.icon

  return (
    <Badge tone={definition.tone} className={className}>
      {!hideIcon && <Icon aria-hidden="true" />}
      {definition.label}
    </Badge>
  )
}

const PRIORITY: Record<string, StatusDefinition> = {
  Low: { label: 'Lav', tone: 'neutral', icon: CircleDashed },
  Normal: { label: 'Normal', tone: 'info', icon: Info },
  High: { label: 'Høj', tone: 'warning', icon: AlertTriangle },
  Critical: { label: 'Kritisk', tone: 'danger', icon: AlertTriangle },
}

export function PriorityBadge({ priority, className }: { priority: string; className?: string }) {
  const definition = PRIORITY[priority]

  if (!definition) {
    return (
      <Badge tone="neutral" className={className}>
        {priority}
      </Badge>
    )
  }

  const Icon = definition.icon

  return (
    <Badge tone={definition.tone} className={className}>
      <Icon aria-hidden="true" />
      <span className="sr-only">Prioritet: </span>
      {definition.label}
    </Badge>
  )
}
